import type { MachineRow, MachineStatus } from "@rally/agreed";
import {
  Button,
  Chip,
  Col,
  Empty,
  Icon,
  ListRow,
  PageHeader,
  Row,
  SkeletonRows,
  Stack,
  Status,
} from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { useStack } from "../chat/panel/panels.tsx";
import { say } from "../localization/say.ts";
import { read } from "../net/rally.ts";
import { goTo } from "../router.ts";
import { machineCard } from "./machine-card.tsx";

function statusTone(status: MachineStatus): "success" | "danger" | "warning" | "neutral" {
  if (status === "active") return "success";
  if (status === "revoked") return "danger";
  if (status === "awaiting") return "warning";
  return "neutral";
}

function statusWord(status: MachineStatus): string {
  return say(`machines.status.${status}` as "machines.status.active");
}

export function Machines() {
  const stack = useStack();
  const [machines, setMachines] = useState<MachineRow[] | null>(null);

  const again = useCallback(() => {
    void read<MachineRow[]>("machine.list")
      .then(setMachines)
      .catch(() => setMachines([]));
  }, []);
  useEffect(again, [again]);

  const here = machines ?? [];
  const live = here.filter((one) => one.status === "active").length;

  return (
    <Stack gap={4} testID="details-machines">
      <PageHeader
        title={say("machines.title")}
        {...(machines === null || here.length === 0 ? {} : { count: here.length })}
        {...(live > 0 ? { action: <Status tone="success">{say("machines.status.active")}</Status> } : {})}
      />

      {machines === null ? <SkeletonRows rows={3} /> : null}

      {machines !== null && here.length === 0 ? (
        <Empty
          fill
          icon="cpu"
          title={say("machines.empty")}
          testID="machines-empty"
          action={
            <Button
              variant="outline"
              size="lg"
              onPress={() => goTo("/me/agents")}
              testID="machines-go-agents"
            >
              {say("machines.goAgents")}
            </Button>
          }
        >
          {`${say("machines.emptyWhy")} ${say("machines.registrationUnavailable")}`}
        </Empty>
      ) : null}

      {here.length > 0 ? (
        <Col>
          {here.map((machine, index) => (
            <ListRow
              key={machine.id}
              title={machine.label}
              value={machine.fingerprint}
              lead={<Icon name="hard-drive" size={16} color="text.secondary" />}
              last={index === here.length - 1}
              testID={`details-machine-${machine.id}`}
              onPress={() => stack.open(machineCard(machine))}
              tag={<Status tone={statusTone(machine.status)}>{statusWord(machine.status)}</Status>}
              note={
                machine.capabilities.length > 0 ? (
                  <Row gap={1} wrap>
                    {machine.capabilities.map((one) => (
                      <Chip key={one} size="sm" muted>
                        {one}
                      </Chip>
                    ))}
                  </Row>
                ) : null
              }
            />
          ))}
        </Col>
      ) : null}
    </Stack>
  );
}
